import React, { useEffect, useState } from 'react';
import { Text } from '../../../../ui';
import {
  KoobButton,
  KoobConfirmationModal,
  KoobDataTable,
  KoobInputCheckbox,
  KoobInputNumber,
  KoobTableAction,
  KoobModal,
  KoobId,
} from '@koob/ui';
import { useTranslation } from 'react-i18next';
import HotelDetailForm from './HotelDetailForm';
import ProductStateBadge from './ProductStateBadge';

export default function HotelsSelected({
  selectedHotels,
  setHotelAttribute,
  onRemoveHotel,
  onRemoveSelectedHotels,
  dmcOrganization,
  toOrganization,
  isAllowedToManage,
}) {
  const { t } = useTranslation('toConnectionRequest');

  const [showHotelDetailsModal, setShowHotelDetailsModal] = useState(false);
  const [hotel, setHotel] = useState(undefined);
  const [checkedIds, setCheckedIds] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const rows = selectedHotels || [];

  const validCheckedIds = checkedIds.filter((id) =>
    rows.some((row) => row.id === id),
  );

  useEffect(() => {
    if (validCheckedIds.length !== checkedIds.length) {
      setCheckedIds(validCheckedIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const toggleRow = (id) => {
    setCheckedIds((current) =>
      current.includes(id)
        ? current.filter((checkedId) => checkedId !== id)
        : [...current, id],
    );
  };

  const toggleAll = () => {
    setCheckedIds(
      validCheckedIds.length === rows.length ? [] : rows.map((row) => row.id),
    );
  };

  const handleConfirmRemove = () => {
    onRemoveSelectedHotels(validCheckedIds);
    setCheckedIds([]);
    setShowConfirm(false);
  };

  const columns = [
    ...(isAllowedToManage && onRemoveSelectedHotels && rows.length > 0
      ? [
          {
            key: '__checked',
            width: '30px',
            label: (
              <KoobInputCheckbox
                checked={validCheckedIds.length === rows.length}
                indeterminate={
                  validCheckedIds.length > 0 &&
                  validCheckedIds.length < rows.length
                }
                onCheckedChange={toggleAll}
              />
            ),
            render: (node) => (
              <KoobInputCheckbox
                checked={validCheckedIds.includes(node.id)}
                onCheckedChange={() => toggleRow(node.id)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'displayName',
      label: t('hotelName'),
      width: '80px',
      render: (node) => (
        <div className="flex flex-col items-start">
          <span className="font-medium">{node?.displayName}</span>
          <KoobId id={node?.id} />
        </div>
      ),
    },
    {
      key: 'country',
      label: t('countryName'),
      width: '60px',
      render: (node) => node?.country,
    },
    {
      key: 'city',
      label: t('cityName'),
      width: '60px',
      render: (node) => node?.city,
    },
    {
      key: 'state',
      label: t('state'),
      width: '70px',
      render: (node) => <ProductStateBadge state={node?.state} />,
    },
    {
      key: 'margin',
      label: t('margin'),
      width: '40px',
      render: (node) => (
        <KoobInputNumber
          min={0}
          value={node?.margin}
          onValueChange={(value) =>
            setHotelAttribute(node.id, 'margin', value ?? '')
          }
          disabled={!isAllowedToManage}
        />
      ),
    },
    {
      key: 'isMarginPercent',
      label: t('percent'),
      width: '60px',
      render: (node) => (
        <input
          type="checkbox"
          checked={node?.isMarginPercent}
          onChange={() =>
            setHotelAttribute(node.id, 'isMarginPercent', !node.isMarginPercent)
          }
          disabled={!isAllowedToManage}
        />
      ),
    },
  ];

  const actions = isAllowedToManage
    ? (node) => (
        <>
          <KoobTableAction
            icon={<i className="fa-regular fa-bed" />}
            label={t('hotelDistribution')}
            onClick={() => {
              setHotel(node);
              setShowHotelDetailsModal(true);
            }}
            data-testid={`${node?.displayName.split(' ').join('-')}-rooms`}
          />
          <KoobTableAction
            icon="delete"
            variant="danger"
            label={t('misc:delete')}
            onClick={() => onRemoveHotel(node.id)}
          />
        </>
      )
    : undefined;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Text type="h3">
          {t('selectedHotels')} ({rows.length})
        </Text>

        {onRemoveSelectedHotels && validCheckedIds.length > 0 && (
          <KoobButton
            variant="danger"
            size="sm"
            onClick={() => setShowConfirm(true)}
          >
            {t('removeSelected')} ({validCheckedIds.length})
          </KoobButton>
        )}
      </div>

      <KoobDataTable columns={columns} data={rows} actions={actions} />

      {showConfirm && (
        <KoobConfirmationModal
          open={showConfirm}
          setOpen={setShowConfirm}
          title={t('removeSelectedConfirmTitle')}
          description={t('removeSelectedConfirmDescription', {
            count: validCheckedIds.length,
          })}
          danger
          onConfirm={handleConfirmRemove}
        />
      )}

      {showHotelDetailsModal && (
        <KoobModal
          open={true}
          setOpen={() => setShowHotelDetailsModal(false)}
          widthClass="sm:max-w-xl xl:max-w-4xl overflow-visible"
        >
          <HotelDetailForm
            hotel={hotel}
            onSelectionFinished={() => setShowHotelDetailsModal(false)}
            dmcOrganization={dmcOrganization}
            toOrganization={toOrganization}
            setHotelAttribute={setHotelAttribute}
          />
        </KoobModal>
      )}
    </div>
  );
}
